import React, { useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { usePostsStore } from '../stores/postsStore';
import { useDialogStore } from '../stores/dialogStore';
import RichTextEditorWithMentions from './RichTextEditorWithMentions';
import Reactions from './Reactions';
import ProfileAvatar from './ProfileAvatar';

/**
 * Reply component supporting nested replies up to 3 levels (depth 0, 1, 2)
 */
const Reply = ({ postId, reply, parentReplyId, depth = 0, onReplyCreated }) => {
  const { accessToken, profile } = useAuthStore();
  const { createReply, deleteReply } = usePostsStore();
  const { confirm, alert } = useDialogStore();

  const [isReplying, setIsReplying] = useState(!reply); // If no reply prop, this is a create form
  const [replyBody, setReplyBody] = useState('');
  const [showNestedReplyForm, setShowNestedReplyForm] = useState(false);

  const isOwner = reply && profile?.id === reply.profile_id;
  const canNestFurther = depth < 2; // Max depth is 2 (0, 1, 2 = 3 levels)

  // Convert @mentions to clickable links
  const convertMentionsToLinks = (text) => {
    if (!text) return '';

    // Replace @DisplayName with clickable links (without the @)
    return text.replace(/@(\w+)/g, (match, displayName) => {
      // Find the profile from reply tags to get the profile ID
      const taggedProfile = reply?.tags?.find(tag => tag.display_name === displayName);
      if (taggedProfile && taggedProfile.id) {
        return `<a href="/profile/${taggedProfile.id}/page/dashboard" class="text-primary-600 dark:text-primary-400 hover:underline font-semibold">${displayName}</a>`;
      }
      // Fallback if no tag found (shouldn't happen)
      return `<span class="text-primary-600 dark:text-primary-400 font-semibold">${displayName}</span>`;
    });
  };

  const handleCreateReply = async () => {
    if (!replyBody.trim()) {
      alert('Error', 'Reply cannot be empty');
      return;
    }

    const replyData = {
      body: replyBody,
      parent_reply_id: parentReplyId
    };

    const result = await createReply(accessToken, postId, replyData);

    if (result.success) {
      setReplyBody('');
      setIsReplying(false);
      setShowNestedReplyForm(false);
      if (onReplyCreated) {
        onReplyCreated();
      }
    } else {
      alert('Error', result.message || 'Failed to create reply');
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm(
      'Delete Reply',
      'Are you sure you want to delete this reply? This will also delete all nested replies.'
    );

    if (!confirmed) return;

    const result = await deleteReply(accessToken, postId, reply.id);

    if (!result.success) {
      alert('Error', result.message || 'Failed to delete reply');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  // If this is a create form (no reply prop)
  if (!reply) {
    return (
      <div className="pl-0">
        <RichTextEditorWithMentions
          value={replyBody}
          onChange={setReplyBody}
          postId={postId}
          placeholder="Write a reply..."
        />
        <div className="flex gap-2 mt-2">
          <button
            onClick={handleCreateReply}
            className="px-4 py-2 bg-primary-600 text-white text-sm rounded hover:bg-primary-700"
          >
            Post Reply
          </button>
          {onReplyCreated && (
            <button
              onClick={() => {
                setReplyBody('');
                if (onReplyCreated) onReplyCreated();
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    );
  }

  // Display existing reply
  return (
    <div className={`${depth > 0 ? 'ml-8 pl-4 border-l-2 border-gray-200 dark:border-gray-700' : ''}`}>
      <div className="surface rounded-lg p-4">
        {/* Reply Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <ProfileAvatar
              profileId={reply.profile_id}
              displayName={reply.author_display_name}
              size="sm"
            />
            <div>
              <h4 className="font-semibold text-sm text-primary">{reply.author_display_name}</h4>
              <p className="text-xs text-secondary" title={new Date(reply.created_at).toLocaleString()}>
                {formatDate(reply.created_at)}
              </p>
            </div>
          </div>

          {isOwner && (
            <button
              onClick={handleDelete}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              title="Delete reply"
            >
              <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Reply Body */}
        <div
          className="text-primary text-sm mb-3 prose dark:prose-invert max-w-none prose-sm"
          dangerouslySetInnerHTML={{ __html: convertMentionsToLinks(reply.body) }}
        />

        {/* Reactions */}
        <div className="mb-3">
          <Reactions
            postId={postId}
            replyId={reply.id}
            totalLikes={reply.reactions?.likes || 0}
            totalDislikes={reply.reactions?.dislikes || 0}
            userLikeValue={reply.user_reaction?.like_value}
            userDislikeValue={reply.user_reaction?.dislike_value}
          />
        </div>

        {/* Reply Button */}
        {canNestFurther && (
          <button
            onClick={() => setShowNestedReplyForm(!showNestedReplyForm)}
            className="text-xs text-primary hover:text-primary-700"
          >
            {showNestedReplyForm ? 'Cancel' : 'Reply'}
          </button>
        )}

        {/* Nested Reply Form */}
        {showNestedReplyForm && canNestFurther && (
          <div className="mt-3">
            <Reply
              postId={postId}
              parentReplyId={reply.id}
              depth={depth + 1}
              onReplyCreated={() => setShowNestedReplyForm(false)}
            />
          </div>
        )}
      </div>

      {/* Nested Replies */}
      {reply.replies && reply.replies.length > 0 && (
        <div className="mt-3 space-y-3">
          {reply.replies.map((nestedReply) => (
            <Reply
              key={nestedReply.id}
              postId={postId}
              reply={nestedReply}
              parentReplyId={reply.id}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Reply;
